import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const KIND_OPTS = [["workshop", "Workshop"], ["webinar", "Webinar"], ["campaign", "Campaign"], ["cohort", "Cohort"]];
const FAMILY_OPTS = [["elitemind", "EliteMind"], ["tinyapp_income", "TinyAPP Income"], ["bundle", "Bundle"], ["one_time", "One-time"], ["other", "Other"]];

export function EventDialog({ open, onOpenChange, event, onDone }) {
  const [form, setForm] = useState(event || {});
  React.useEffect(() => { setForm(event || {}); }, [event, open]);
  const [saving, setSaving] = useState(false);
  const editing = !!event?.id;
  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        code: form.code, name: form.name, kind: form.kind || "workshop",
        start_date: form.start_date || null, end_date: form.end_date || null,
        notes: form.notes || null,
      };
      if (editing) await api.patch(`/catalogue/events/${event.id}`, payload);
      else await api.post("/catalogue/events", payload);
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="event-dialog">
        <DialogHeader><DialogTitle>{editing ? "Edit event" : "New event"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Event code" v={form.code} onChange={(v) => setForm({ ...form, code: v })} testid="event-code" />
          <Field label="Event name" v={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="event-name" />
          <SelectField label="Kind" v={form.kind || "workshop"} onChange={(v) => setForm({ ...form, kind: v })} options={KIND_OPTS} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date" v={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} type="date" />
            <Field label="End date" v={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} type="date" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="event-submit">{saving ? "Saving…" : (editing ? "Save changes" : "Create event")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ProductDialog({ open, onOpenChange, product, onDone }) {
  const [form, setForm] = useState(product || {});
  React.useEffect(() => { setForm(product || {}); }, [product, open]);
  const [saving, setSaving] = useState(false);
  const editing = !!product?.id;
  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        code: form.code, name: form.name,
        family: form.family || "one_time",
        description: form.description || null,
      };
      if (editing) await api.patch(`/catalogue/products/${product.id}`, payload);
      else await api.post("/catalogue/products", payload);
      onDone?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="product-dialog">
        <DialogHeader><DialogTitle>{editing ? "Edit product" : "New product"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Product code" v={form.code} onChange={(v) => setForm({ ...form, code: v })} testid="product-code" />
          <Field label="Product name" v={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="product-name" />
          <SelectField label="Family" v={form.family || "one_time"} onChange={(v) => setForm({ ...form, family: v })} options={FAMILY_OPTS} />
          <Field label="Description" v={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="product-description" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="product-submit">{saving ? "Saving…" : (editing ? "Save changes" : "Create product")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Package = SKU in the DB. Display-only rename. */
export function PackageDialog({ open, onOpenChange, pkg, product, products, onDone }) {
  const seed = () => ({
    product_id: pkg?.product_id || product?.id || "",
    recognition_policy: pkg?.recognition_policy || "on_payment_date",
    duration_months: pkg?.duration_months ?? 0,
    default_price: pkg?.default_price_minor != null ? (pkg.default_price_minor / 100).toFixed(2) : "",
    code: pkg?.code || "", name: pkg?.name || "",
    default_description: pkg?.default_description || "",
    sql_item_code: pkg?.sql_item_code || "",
  });
  const [form, setForm] = useState(seed);
  React.useEffect(() => { setForm(seed()); /* eslint-disable-line */ }, [pkg, product, open]);
  const [saving, setSaving] = useState(false);
  const editing = !!pkg?.id;
  const submit = async () => {
    const priceStr = String(form.default_price ?? "").trim();
    if (priceStr === "" || Number(priceStr) < 0) {
      toast.error("Enter a default price in MYR (0 or greater).");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        product_id: form.product_id, code: form.code, name: form.name,
        duration_months: parseInt(form.duration_months || 0),
        recognition_policy: form.recognition_policy || "on_payment_date",
        sql_item_code: form.sql_item_code || null,
        default_description: form.default_description || null,
        default_price_minor: Math.round(Number(priceStr) * 100),
      };
      if (editing) await api.patch(`/catalogue/skus/${pkg.id}`, payload);
      else await api.post("/catalogue/skus", payload);
      onDone?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="package-dialog">
        <DialogHeader><DialogTitle>{editing ? "Edit package" : "New package"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {!product && (
            <SelectField label="Product" v={form.product_id} onChange={(v) => setForm({ ...form, product_id: v })}
              options={(products || []).map((p) => [p.id, `${p.code} · ${p.name}`])} />
          )}
          <Field label="Package code" v={form.code} onChange={(v) => setForm({ ...form, code: v })} testid="package-code" />
          <Field label="Package name" v={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="package-name" />
          <Field label="Description" v={form.default_description} onChange={(v) => setForm({ ...form, default_description: v })} testid="package-description" />
          <div>
            <Field label="Default price (MYR) *" v={form.default_price} onChange={(v) => setForm({ ...form, default_price: v })} type="number" placeholder="0.00" testid="package-price" />
            <div className="text-[11px] text-slate-500 mt-1">Base price used in sales, invoices and pro-formas unless overridden by an event offer.</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Duration (months, 0 = one-time)" v={form.duration_months} onChange={(v) => setForm({ ...form, duration_months: v })} type="number" />
            <SelectField label="Recognition" v={form.recognition_policy || "on_payment_date"} onChange={(v) => setForm({ ...form, recognition_policy: v })}
              options={[["monthly_ratable", "Monthly ratable"], ["on_payment_date", "On payment date"], ["on_fulfillment", "On fulfilment"]]} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="package-submit">{saving ? "Saving…" : (editing ? "Save changes" : "Create package")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function Field({ label, v, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider">{label}</Label>
      <Input type={type} value={v ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}

export function SelectField({ label, v, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider">{label}</Label>
      <Select value={v || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue placeholder="Select…" /></SelectTrigger>
        <SelectContent className="z-[80]">
          {options.map(([val, lbl]) => <SelectItem key={val} value={val}>{lbl}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
